import React, { useMemo } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Checkbox, Stack, TableCell, TableCellProps, TableRow } from "@mui/material";
import { SyncIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components/guards";
import { FileIcon } from "components/old-data-box-components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useNavigate } from "hooks";
import { useConnectionsSelect, usePickUserPermissions, useUserRoleSelector } from "store";
import { DataItem, DataroomFileTable } from "store/types";

import { FileType, RoleName } from "../../../constants";

// Debug flag to show connected icon in dataroom column
const SHOW_CONNECTED_ICON_DEBUG = true;

const commonCellStyles: TableCellProps["sx"] = {
  fontSize: "15px",
  fontWeight: "normal",
  height: "40px",
  padding: "6px 16px",
  // whiteSpace: "wr",
  textOverflow: "ellipsis",
  overflow: "hidden",
  borderBottom: "solid 1px #dae0e2"
};

interface FileTableRowProps {
  isSelected: boolean;
  row: DataroomFileTable["rows"][0];
  columns: DataroomFileTable["columns"];
  depth?: number;
  hideTitle?: boolean;
  isRowTransparent?: boolean;
  onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, item: DataItem) => void;
  onFileClick: (tableRow: DataroomFileTable["rows"][0]) => Promise<void>;
}

const allowedFileTypesToPreview = ["png", "jpeg", "jpg", "pdf"];

// e.g. 2023-06-03T00:00:00
const ISO_REGEX = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/;

export const FileTableRow: React.FC<FileTableRowProps> = React.memo(
  ({ row, columns, hideTitle = false, isRowTransparent = false, onCheckboxChange, isSelected, onFileClick }) => {
    const navigate = useNavigate();
    const cloudIntegrations = usePickUserPermissions("cloud_integrations");
    const isViewerPermission = cloudIntegrations && cloudIntegrations === "viewer";
    const userRole = useUserRoleSelector();
    const isAdmin = userRole?.name === RoleName.Admin;
    const connections = useConnectionsSelect();
    const dataroomDealName = ISO_REGEX.test(row.fundName)
      ? Intl.DateTimeFormat().format(new Date(row.fundName))
      : row.fundName;

    // Check if the dataroom is connected
    const isDataroomConnected = useMemo(() => {
      if (!SHOW_CONNECTED_ICON_DEBUG || !row.dataroom) return false;
      // Match by dataroom name
      const dataroomStr = String(row.dataroom).trim();
      const connection = connections.find((conn) => {
        const connTitle = [conn.subdomain, conn.type_title, conn.username].map((val) => String(val || "")).join("");
        const normalize = (s: string) => s.toLowerCase().replace(/[^a-z0-9א-ת]/g, "");
        if (normalize(dataroomStr).includes(normalize(connTitle))) return true;
      });
      return connection?.is_login_valid === true;
    }, [connections, row.dataroom]);

    const customSyncIcon = useMemo(() => {
      const isDownloaded = Boolean(row.downloaded_at && row.downloaded_at !== "" && row.downloaded_at !== "-");
      return (
        <Box sx={{ position: "relative", height: 24 }} component="span">
          <FileIcon
            style={{
              opacity: isDownloaded ? "1" : "0.6",
              fill: isDownloaded ? "#5D7782" : "#BAC7CE"
            }}
            type={FileType.FILE}
          />
          {!isDownloaded && (
            <SyncIcon
              sx={{
                borderRadius: "10px",
                backgroundColor: "white",
                position: "absolute",
                fontSize: "13px",
                top: "16px",
                right: "0px"
              }}
            />
          )}
        </Box>
      );
    }, [row.downloaded_at]);

    return (
      <>
        <TableRow sx={isRowTransparent ? { backgroundColor: "transparent !important" } : {}}>
          <TableCell sx={commonCellStyles}>
            <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]} hideChild={isViewerPermission}>
              <Checkbox
                checked={isSelected}
                onChange={(event) =>
                  onCheckboxChange(event, {
                    id: row.id as string,
                    name: row.name,
                    dealId: row.fundId,
                    fetcherId: row.fetcherId,
                    downloaded_at: row.downloaded_at || null
                  })
                }
              />
            </RoleComponentGuard>
          </TableCell>
          {!hideTitle && (
            <TableCell sx={commonCellStyles}>
              <span>{dataroomDealName}</span>
            </TableCell>
          )}
          {!hideTitle && (
            <TableCell sx={commonCellStyles}>
              <Stack
                onClick={() => {
                  if (!row.dealId) return;
                  navigate({
                    route: "dealWorkspace",
                    params: { deal_id: row.dealId },
                    setQueriesMethod: "drop"
                  });
                }}
                sx={{ ":hover": { cursor: "pointer" } }}
              >
                {row.dealName}
              </Stack>
            </TableCell>
          )}
          {columns.map((column) => {
            let disabledFileForPreview: boolean = false;

            if (column.key === "name") {
              const { name: fileName } = row;
              const fileType = fileName.split(".").at(-1);

              if (fileType) {
                disabledFileForPreview = !allowedFileTypesToPreview.some((type) => type === fileType);
              }
            }

            return (
              <TableCell
                {...(column.key === "name" && { onClick: () => onFileClick(row) })}
                sx={commonCellStyles}
                key={column.key}
                align="left"
              >
                <Box
                  sx={{
                    height: "max-content",
                    display: "flex",
                    alignItems: "center",
                    columnGap: "2px",
                    ...(!disabledFileForPreview && { cursor: "pointer" })
                  }}
                >
                  {disabledFileForPreview && (
                    <MaterialTooltip isShow title="Unsupported file format. Please download to view.">
                      <InfoOutlinedIcon fontSize="sm" />
                    </MaterialTooltip>
                  )}
                  {column.key === "name" && customSyncIcon}
                  {column.key === "dataroom" && SHOW_CONNECTED_ICON_DEBUG && isAdmin && (
                    <Box
                      component="span"
                      title={isDataroomConnected ? `Connected (${row.dataroom})` : `Not Connected (${row.dataroom})`}
                      sx={{ display: "inline-flex", alignItems: "center", marginRight: "4px" }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: "16px",
                          color: isDataroomConnected ? "#4caf50" : "#9e9e9e"
                        }}
                      />
                    </Box>
                  )}
                  {typeof row[column.key as keyof DataroomFileTable["rows"][0]] === "number"
                    ? Math.round(row[column.key as keyof DataroomFileTable["rows"][0]] as number).toLocaleString()
                    : row[column.key as keyof DataroomFileTable["rows"][0]]}
                </Box>
              </TableCell>
            );
          })}
        </TableRow>
      </>
    );
  }
);
